/**
 * The operation registry: the index only. Each operation lives in its own file under
 * `operations/`, because there are ~280 of them across the two APIs (30 Task API, 255 Web API) and
 * one file would be unreadable.
 *
 * Both specs are published at
 *   https://approval.aws.stag.visma.net/apidocs/specs/task-api.json  (basePath /approval/rest)
 *   https://approval.aws.stag.visma.net/apidocs/specs/web-api.json   (basePath /approval/jaxrs)
 * and `tools/generate-registry.mjs` turns them into a skeleton to curate - see README.
 *
 * ## Operation shape
 *
 *   id, label, summary
 *   mutating: true            POST/PUT/DELETE. Never run as a pair; see ApiTool.component.jsx.
 *   params: [{
 *       name, label, help
 *       in: "path" | "query"  path params are substituted into `path`, and are required
 *       appliesTo: ["task", "web"]
 *       options: "companies" | "tasks"    dropdown fetched from the API
 *       choices: ["active", …]            dropdown of fixed values, for a spec enum
 *       defaultValue                      pre-filled, so both sides start comparable
 *   }]
 *   sides: { task?, web? }    either may be omitted - plenty of operations exist on one API only
 *   compare: { key, aliases, derived, ignore, notes }   see compare.js#buildFieldMap
 *
 * ## Side shape
 *
 *   method, path              path may contain {placeholders} matching a path param
 *   accept                    defaults to application/json
 *   query(params)             optional; empty values are dropped
 *   body(params), bodyType    optional; bodyType is "json" (default) or "form"
 *   extract(body)             always returns an array, so a single object compares like a list
 *   operationId, scope, shape documentation only, shown in the UI
 */
import {myHistory} from "./operations/myHistory.js";
import {myTasks} from "./operations/myTasks.js";

export const API_LABELS = {task: "Task API (Visma)", web: "Web API (Visma)"};

/**
 * Mounted under the app's own API root (`window.ENVIRONMENT.APPROVAL_API_OAUTH`, which already
 * ends in `/approval`), so these are the `basePath` of each swagger spec minus that prefix.
 */
export const API_BASE_PATHS = {task: "/rest", web: "/jaxrs"};

export const endpoints = [
    myTasks,
    myHistory,
];

/** Pre-filled parameter values, so a fresh operation starts with both sides comparable. */
export function defaultParams(endpoint) {
    return Object.fromEntries(endpoint.params
        .filter(param => param.defaultValue != null)
        .map(param => [param.name, String(param.defaultValue)]));
}

export function getEndpoint(id) {
    return endpoints.find(endpoint => endpoint.id === id);
}

/** The sides an operation actually has, in a stable order. */
export function sideKeys(endpoint) {
    return ["task", "web"].filter(key => endpoint.sides[key] != null);
}

/** True when both APIs expose the operation, so a run can produce a comparison. */
export function isPaired(endpoint) {
    return sideKeys(endpoint).length === 2;
}

/**
 * Path parameters with no value yet. A call cannot be built without them, so the UI blocks the run
 * and says which are missing rather than requesting `/tasks/undefined`.
 */
export function missingParams(endpoint, params) {
    return endpoint.params
        .filter(param => param.in === "path")
        .filter(param => String(params[param.name] ?? "").trim() === "")
        .map(param => param.name);
}
